import { useState, useCallback } from "react";
import PageShell from "../../../components/PageShell";
import DataTable from "../../../components/DataTable";
import DataForm from "../../../components/DataForm";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import ConfirmDialog from "../../../components/ConfirmDialog";
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
} from "../../../components/ui/context-menu";
import {
  useFlexibleDiscounts,
  useSaveFlexibleDiscount,
  useArchiveFlexibleDiscount,
} from "../hooks/useFlexibleDiscounts";
import toast from "react-hot-toast";
import { useTranslation } from "../../../i18n";

const COLUMNS = [
  // All headers will be translated via DataTable's t() using the header value as key
  { header: "ID", accessor: "id", type: "number" },
  { header: "col.index", accessor: "renderIndex", type: "number" },
  { header: "col.name", accessor: "name", type: "text" },
  {
    header: "col.amount",
    accessor: "amount",
    type: "percent",
    mapper: (v) => `${Number(v * 100)}%`,
  },
  { header: "col.description", accessor: "description", type: "text" },
  { header: "col.available", accessor: "available", type: "boolean" },
];

const FORM_FIELDS = [
  // Labels are handled by DataForm which uses t() to translate them
  { name: "id", label: "form.id", type: "number", disabled: true },
  { name: "renderIndex", label: "form.index", type: "number" },
  { name: "name", label: "form.name", type: "text" },
  { name: "amount", label: "form.amount", type: "percent" },
  { name: "description", label: "form.description", type: "text" },
  {
    name: "available",
    label: "form.available",
    type: "boolean",
    required: false,
    default: true,
  },
];

const CREATE_DEFAULTS = {
  name: "",
  description: "",
  renderIndex: "100",
  amount: "0%",
  available: true,
};

export default function FlexibleDiscountPage() {
  const { t } = useTranslation();
  const { data: discounts, isLoading } = useFlexibleDiscounts();
  const saveMutation = useSaveFlexibleDiscount();
  const archiveMutation = useArchiveFlexibleDiscount();
  const [editingRow, setEditingRow] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [values, setValues] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);

  const openEdit = useCallback((row) => {
    setEditingRow(row);
    setValues({
      id: row.id,
      renderIndex: row.renderIndex,
      name: row.name,
      description: row.description,
      amount: row.amount != null ? `${Number(row.amount * 100)}%` : "0%",
      available: row.available ?? true,
    });
    setIsNew(false);
  }, []);

  const openCreate = useCallback(() => {
    setEditingRow(CREATE_DEFAULTS);
    setValues({ ...CREATE_DEFAULTS });
    setIsNew(true);
  }, []);

  const handleSave = () => {
    if (!values) return;
    saveMutation.mutate(values, {
      onSuccess: () => {
        toast.success(
          isNew ? t("toast.recordCreated") : t("toast.recordUpdated"),
        );
        setEditingRow(null);
        setValues(null);
      },
      onError: () => toast.error(t("toast.recordSaveFailed")),
    });
  };

  const renderArchiveMenu = useCallback(
    (row, children) => (
      <ContextMenu>
        <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem
            className="bg-c-red-500 hover:bg-c-red-600 text-white"
            onClick={() => setArchiveTarget(row)}
          >
            {t("common.archive")}
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    ),
    [],
  );

  const handleArchive = () => {
    if (!archiveTarget) return;
    archiveMutation.mutate(archiveTarget.id, {
      onSuccess: () => {
        toast.success(t("toast.discountArchived"));
        setArchiveTarget(null);
      },
      onError: () => toast.error(t("toast.discountArchiveFailed")),
    });
  };

  return (
    <PageShell
      title={t("page.flexibleDiscounts")}
      actions={<Button onClick={openCreate}>{t("common.add")}</Button>}
    >
      {isLoading ? (
        <p className="text-c-gray-400">{t("common.loading")}</p>
      ) : (
        <DataTable
          columns={COLUMNS}
          data={discounts}
          onRowClick={openEdit}
          renderContextMenu={renderArchiveMenu}
        />
      )}
      <Dialog
        open={!!editingRow}
        onOpenChange={(o) => {
          if (!o) {
            setEditingRow(null);
            setValues(null);
          }
        }}
      >
        <DialogContent className="left-auto right-0 top-0 translate-x-0 translate-y-0 h-dvh max-h-none rounded-none sm:rounded-none border-r-0 border-t-0 border-b-0 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right min-w-[480px] grid-rows-[auto_1fr_auto]">
          <DialogHeader>
            <DialogTitle>
              {isNew ? t("dialog.addDiscount") : t("dialog.editRecord")}
            </DialogTitle>
          </DialogHeader>
          <form
            id="edit-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
            className="flex-1 overflow-y-auto px-2"
          >
            <DataForm
              fields={FORM_FIELDS}
              values={values}
              onChange={setValues}
            />
          </form>
          <DialogFooter className="flex gap-2">
            <Button type="submit" form="edit-form">
              {t("common.save")}
            </Button>
            {!isNew && (
              <Button
                variant="destructive"
                onClick={() => setArchiveTarget(editingRow)}
              >
                {t("common.archive")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={!!archiveTarget}
        onOpenChange={(o) => {
          if (!o) setArchiveTarget(null);
        }}
        itemName={`${t("page.flexibleDiscounts").toLowerCase()} "${archiveTarget?.name}"`}
        onConfirm={handleArchive}
      />
    </PageShell>
  );
}
